import { getErrorMessage } from "@captions-web/dev-logger";
import { getLanguageLocale } from "@captions-web/i18n/utils/getLanguageLocale";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import i18n from "i18next";
import { z } from "zod";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { SessionEvents } from "~/lib/analytics/defs/SessionEvents";
import { createQueryRequestDefinition } from "~/lib/network/definitions/create";
import { UserProfileOrgType, UserProfileType } from "~/modules/auth/network";

import { getConfigStore } from "./config";
import { createStoreGetters } from "./root";

const SUPPORT_EMAIL = "hello@captions.ai";
const SUPPORT_EMAIL_SUBJECT = "Mirage Studio Support Request";

export function createIntercomStore() {
  return createStore(
    combine(
      {
        intercom: createIntercom(),
        visible: false,
        ready: false,
      },
      (set, get) => {
        function bootIntercom(userInfo?: IntercomUserInfo) {
          const { intercom } = get();
          if (!intercom.isLoaded()) {
            intercom.load(getConfigStore().env.INTERCOM_APP_ID);
            set({ ready: true });
          }

          intercom.boot({
            hide_default_launcher: true,
            alignment: "right",
            horizontal_padding: 34,
            vertical_padding: 86,

            app_id: getConfigStore().env.INTERCOM_APP_ID,
            language_override: getLanguageLocale(i18n),

            ...userInfo,
          });
        }

        return {
          async init() {
            const { intercom } = get();
            i18n.on("languageChanged", () => {
              if (intercom.isLoaded()) {
                intercom.update({
                  language_override: getLanguageLocale(i18n),
                });
              }
            });
          },

          async boot(user?: UserProfileType, org?: UserProfileOrgType) {
            try {
              if (user) {
                const { userHash } = await getUserHashQuery.trigger({});
                bootIntercom({
                  user_hash: userHash,
                  user_id: user.userId,
                  email: user.email,
                  name: user.name,
                  company: {
                    id: org?.id,
                    name: org?.name,
                  },
                });
              } else {
                bootIntercom();
              }
            } catch (error) {
              SessionEvents.intercom_load_failed.track({
                error_message: getErrorMessage(error),
              });
            }
          },
          update() {
            get().intercom.update();
          },
          shutdown() {
            get().intercom.shutdown();
          },
          toggleWidget() {
            const { visible, intercom } = get();
            if (visible) {
              intercom.hide();
              set({ visible: false });
            } else {
              intercom.show();
              set({ visible: true });
              SessionEvents.intercom_opened.track();
            }
          },
        };
      }
    )
  );
}

type IntercomStore = ReturnType<ReturnType<typeof createIntercomStore>["getState"]>;

export const {
  getStore: getIntercomStore,
  getStoreAsync: getIntercomStoreAsync,
  getStoreApi: getIntercomStoreApi,
} = createStoreGetters<IntercomStore>("intercom");

export const useIntercomStore = createUseStoreWithSelectorDeferred(() => getIntercomStoreApi());

// copied from previous intercom context in web app

const getUserHashQuery = createQueryRequestDefinition({
  queryKey: ["intercom-hash"],
  host: "web-core",
  method: "GET",
  path: "account/v1/intercom-id-verification",
  responseSchema: z.object({ userHash: z.string() }),
});

interface IntercomAvatar {
  type?: "avatar";
  image_url?: string;
}

interface IntercomCompany {
  id?: string;
  name?: string;
  created_at?: number;
  plan?: string;
  monthly_spend?: number;
  user_count?: number;
  size?: number;
  website?: string;
  industry?: string;
}

interface IntercomUserInfo {
  user_id?: string;
  user_hash?: string;

  email?: string;
  name?: string;
  phone?: string;
  avatar?: IntercomAvatar;

  company?: IntercomCompany;
  companies?: IntercomCompany[];
}

interface IntercomSettings extends IntercomUserInfo {
  app_id: string;
  custom_launcher_selector?: string;
  alignment?: "left" | "right";
  horizontal_padding?: number;
  vertical_padding?: number;
  hide_default_launcher?: boolean;
  session_duration?: number;
  action_color?: string;
  background_color?: string;
  created_at?: number;
  unsubscribed_from_emails?: boolean;
  language_override?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  page_title?: string;
}

const createIntercom = () => {
  const _call = <A>(method: string, arg?: A) => {
    "Intercom" in window && typeof window.Intercom === "function" && window.Intercom(method, arg);
  };

  return {
    isLoaded() {
      return "Intercom" in window && typeof window.Intercom === "function";
    },

    // Loads Intercom with the snippet
    // This must be run before boot, it initializes window.Intercom
    load(appId: string) {
      const Intercom = "Intercom" in window && window.Intercom;
      if (typeof Intercom === "function") {
        const settings =
          "intercomSettings" in window ? (window.intercomSettings as IntercomSettings) : undefined;
        Intercom("reattach_activator");
        Intercom("update", settings);
      } else {
        const i = function (...args: unknown[]) {
          i.c(args);
        };
        i.q = [] as unknown[];
        i.c = function (args: unknown[]) {
          i.q.push(args);
        };
        Object.assign(window, { Intercom: i });
        const l = function () {
          const s = document.createElement("script");
          s.type = "text/javascript";
          s.async = true;
          s.src = "https://widget.intercom.io/widget/" + appId;
          const x = document.getElementsByTagName("script")[0];
          x.parentNode?.insertBefore(s, x);
        };
        if (document.readyState === "complete") {
          l();
        } else if ("attachEvent" in window && typeof window.attachEvent === "function") {
          window.attachEvent("onload", l);
        } else {
          window.addEventListener("load", l, false);
        }
      }
    },

    show() {
      try {
        _call("show");
      } catch {
        window.open(`mailto:${SUPPORT_EMAIL}?subject=${SUPPORT_EMAIL_SUBJECT}`, "_blank");
      }
    },

    shutdown: () => _call("shutdown"),

    hide: () => _call("hide"),

    update: (options?: Partial<IntercomSettings>) => _call("update", options),

    boot: (settings: IntercomSettings) => _call("boot", settings),

    onShow: (callback: () => void) => _call("onShow", callback),

    onHide: (callback: () => void) => _call("onHide", callback),

    onUnreadCountChange: (callback: (unreadCount: number) => void) =>
      _call("onUnreadCountChange", callback),
  };
};
